import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import {
  amountDivideDecimals,
  findChainInfo,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";
import { GetListRes } from "../types/GetListRes";
import {
  SweepDestGroupType,
  SweepDestStatus,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { TableColumnKey, TableRow } from "../types/Table";
import { useAssets } from "src/components/FetchConfig";

interface UseTableRowsProps {
  apiList: ApiResult<GetListRes>;
}

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTableRows = (props: UseTableRowsProps) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te, translate } = useTranslation(translatePrefix);
  const { apiList } = props;

  const { selectAssetsByChain } = useAssets();

  const getSweepDestStatus = (status: number) => {
    return translate(`enumAutoSweepDestStatus.${SweepDestStatus[status]}`);
  };

  const getGroupType = (groupType: number) => {
    return te(SweepDestGroupType[groupType] as "Fallback" | "Normal");
  };

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((row) => {
      const assets = selectAssetsByChain({
        chain_type: row.chain_type,
        chain_id: row.chain_id,
      });
      const selectedAsset = assets.find(
        (item) => item.asset_name === row?.asset_name,
      );
      const getAmount = (amount: string | undefined | null) => {
        if (amount === null || amount === undefined) {
          return "-";
        }
        if (amount === "0") {
          return t("unlimited");
        }
        return amountDivideDecimals(amount, selectedAsset?.decimals);
      };
      const mappedResult = [
        [
          TableColumnKey.GroupSequence,
          t(TableColumnKey.GroupSequence),
          row.group_seq,
        ],
        [TableColumnKey.GroupName, t(TableColumnKey.GroupName), row.group_name],
        [
          TableColumnKey.GroupType,
          t(TableColumnKey.GroupType),
          getGroupType(row.group_type),
        ],
        [
          TableColumnKey.Status,
          t(TableColumnKey.Status),
          getSweepDestStatus(row.status),
        ],
        [
          TableColumnKey.Active,
          t(TableColumnKey.Active),
          row.is_active ? te("Yes") : te("No"),
        ],
        [
          TableColumnKey.Valid,
          t(TableColumnKey.Valid),
          row.is_valid ? te("Yes") : te("No"),
        ],
        [
          TableColumnKey.ChainName,
          t(TableColumnKey.ChainName),
          findChainInfo(row.chain_type?.toString(), row.chain_id)?.name,
        ],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), row.asset_name],
        [
          TableColumnKey.MaximumAmountLimit,
          t(TableColumnKey.MaximumAmountLimit),
          getAmount(row.max_amount_limit),
        ],
        [
          TableColumnKey.RotateAmountLimit,
          t(TableColumnKey.RotateAmountLimit),
          getAmount(row.rotate_group_amount_limit),
        ],
        [
          TableColumnKey.CreatedBy,
          t(TableColumnKey.CreatedBy),
          row.updated_by, //created by not exist
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(row.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(row.last_modified_date),
        ],
        ["rawData", "", row],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTableRows;
